import { type HomePageEndpointResponse, MarketStatus } from '@ps/isp-web-services-types';
import { notEmpty } from '@ps/web-core-utils';
import produce from 'immer';

import type { EventTypeId } from '@swf/sports/types/eventType';
import type { MarketPollingData } from '@swf/sports/types/market';
import updateMarket from '@swf/sports/utils/updateMarket';

import { defaultState } from './defaultState';

type State = typeof defaultState;

export const reducers = {
  clear: (): State => produce(defaultState, (draft) => draft),
  homePageRequested: (state: State): State => ({
    ...state,
    isLoading: true
  }),
  setSelectedPopularAccumulatorsEventType: (state: State, payload: EventTypeId): State => ({
    ...state,
    selectedPopularAccumulatorsEventType: payload
  }),
  setSelectedInPlayEventType: (state: State, payload: EventTypeId): State => ({
    ...state,
    selectedInPlayEventType: payload
  }),
  homePageLoadSuccess: (state: State, payload: HomePageEndpointResponse): State => {
    let { selectedPopularAccumulatorsEventType, selectedInPlayEventType } = state;

    const validEventTypes = payload.popularAccumulators.map(
      (pacca) => payload.markets[pacca.runnerIds[0].marketId].eventTypeId
    );

    if (
      (!selectedPopularAccumulatorsEventType && payload.popularAccumulators.length) ||
      (selectedPopularAccumulatorsEventType &&
        !validEventTypes.includes(selectedPopularAccumulatorsEventType) &&
        payload.popularAccumulators.length)
    ) {
      const market = payload.markets[payload.popularAccumulators[0].runnerIds[0].marketId];
      selectedPopularAccumulatorsEventType = market.eventTypeId;
    }

    if (
      state.inPlayEvents.length &&
      (!selectedInPlayEventType ||
        !payload.inPlayEvents.find((tab) => tab.eventTypeId === selectedInPlayEventType))
    ) {
      selectedInPlayEventType = payload.inPlayEvents[0].eventTypeId;
    }

    return {
      ...state,
      ...payload,
      isLoading: false,
      hasPreloaded: true,
      selectedPopularAccumulatorsEventType,
      selectedInPlayEventType,
      error: false
    };
  },
  homePageLoadFailure: (state: State): State => ({
    ...state,
    isLoading: false,
    hasPreloaded: true,
    error: true
  }),
  marketsUpdated: (state: State, { data: marketUpdates, closed }: MarketPollingData) =>
    produce(state, (draft) => {
      Object.entries(marketUpdates).forEach(([marketUpdateId, marketUpdate]) => {
        Object.values(draft.markets)
          .filter(notEmpty)
          .forEach((market) => {
            if (closed.includes(market.marketId)) {
              market.marketStatus = MarketStatus.CLOSED;
              return;
            }
            if (market.marketId === marketUpdateId && marketUpdate) {
              updateMarket(market, marketUpdate);
            }
          });
      });
      return draft;
    })
};
